/**
 * Merchant-review hooks.
 *
 * `/admin/merchants/{id}/reviews` is fully typed; the summary endpoint
 * (`/merchants/{id}/reviews/summary`) is not — handled with `as never`.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';
import { merchantOpsKeys, unwrap } from './keys';

export type ReviewPage = components['schemas']['PageResponseReviewResponse'];

export interface MerchantReviewSummary {
  averageRating?: number;
  totalReviews?: number;
}

export function useMerchantReviewsSummary(
  merchantId: string | null,
): UseQueryResult<MerchantReviewSummary, Error> {
  return useQuery<MerchantReviewSummary, Error>({
    queryKey: merchantOpsKeys.reviewsSummary(merchantId ?? 'none'),
    enabled: merchantId !== null,
    queryFn: async () => {
      if (!merchantId) throw new Error('Selecione um estabelecimento');
      const { data, error } = await api.GET(
        '/api/v1/merchants/{id}/reviews/summary' as never,
        { params: { path: { id: merchantId } } } as never,
      );
      return unwrap(data as MerchantReviewSummary | undefined, error);
    },
  });
}

export function useMerchantReviews(
  merchantId: string | null,
  page: number,
  size: number,
): UseQueryResult<ReviewPage, Error> {
  return useQuery<ReviewPage, Error>({
    queryKey: merchantOpsKeys.reviews(merchantId ?? 'none', page, size),
    enabled: merchantId !== null,
    queryFn: async () => {
      if (!merchantId) throw new Error('Selecione um estabelecimento');
      const { data, error } = await api.GET('/api/v1/admin/merchants/{id}/reviews', {
        params: { path: { id: merchantId }, query: { page, size } },
      });
      return unwrap(data, error);
    },
  });
}
